import React, { useState, useEffect, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPhoneAlt, faWeightHanging } from "@fortawesome/free-solid-svg-icons";
import CollapsibleSection from "../components/common/CollapsibleSection";
import deliData from "../db/data/deliData";
import { applyOverridesToData } from "../utils/priceOverrides";
import { usePriceOverrides } from "../context/PriceOverridesContext";
import "./MenuPage.css";
import "./DeliMenuPage.css";

const DeliMenuPage = () => {
  // Track which section is visible in the viewport (for nav highlighting)
  const [activeSection, setActiveSection] = useState("meats");
  
  // Track which sections should be expanded (separate from visibility)
  const [expandedSections, setExpandedSections] = useState({
    meats: true // Only expand meats by default
  });
  const sectionRefs = useMemo(() => ({
    "meats": React.createRef(),
    "cheeses": React.createRef(),
    "breads": React.createRef(),
    "condiments": React.createRef(),
    "fish": React.createRef(),
    "salads": React.createRef(),
    "saladPlates": React.createRef(),
    "soups": React.createRef(),
    "specialties": React.createRef(),
    "treats": React.createRef(),
    "fridge": React.createRef()
  }), []);

  const { overrides } = usePriceOverrides();
  const dataWithOverrides = useMemo(() => {
    return applyOverridesToData("deliData", deliData, overrides);
  }, [overrides]);

  // Setup intersection observer for sections
  useEffect(() => {
    const observerOptions = {
      rootMargin: "-130px 0px -65% 0px", // Adjusted to be just below sticky nav, detection zone extends to 35% of viewport height
      threshold: 0.1, // Section needs to be 10% visible in this zone
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    }, observerOptions);

    // Observe all sections
    Object.values(sectionRefs).forEach((ref) => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });

    return () => {
      Object.values(sectionRefs).forEach((ref) => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      });
    };
  }, [sectionRefs]);

  // Scroll to section when clicking nav link
  const scrollToSection = (sectionId) => {
    // Set the active section (for nav highlighting)
    setActiveSection(sectionId);
    
    // Expand the clicked section
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: true
    }));
    
    // Scroll to the section
    const section = sectionRefs[sectionId].current;
    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
    }
  };
  
  // Toggle a section when its header is clicked
  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // Simple NavBar component for this page
  const DeliNavBar = () => (
    <div className="menu-nav">
      <div className="container">
        <ul className="menu-nav-list">
          <li>
            <button
              className={activeSection === "meats" ? "active" : ""}
              onClick={() => scrollToSection("meats")}
            >
              Meats
            </button>
          </li>
          <li>
            <button
              className={activeSection === "cheeses" ? "active" : ""}
              onClick={() => scrollToSection("cheeses")}
            >
              Cheeses
            </button>
          </li>
          <li>
            <button
              className={activeSection === "breads" ? "active" : ""}
              onClick={() => scrollToSection("breads")}
            >
              Breads
            </button>
          </li>
          <li>
            <button
              className={activeSection === "condiments" ? "active" : ""}
              onClick={() => scrollToSection("condiments")}
            >
              Condiments
            </button>
          </li>
          <li>
            <button
              className={activeSection === "fish" ? "active" : ""}
              onClick={() => scrollToSection("fish")}
            >
              Smoked Fish
            </button>
          </li>
          <li>
            <button
              className={activeSection === "salads" ? "active" : ""}
              onClick={() => scrollToSection("salads")}
            >
              Salads
            </button>
          </li>
          <li>
            <button
              className={activeSection === "saladPlates" ? "active" : ""}
              onClick={() => scrollToSection("saladPlates")}
            >
              Salad Plates
            </button>
          </li>
          <li>
            <button
              className={activeSection === "soups" ? "active" : ""}
              onClick={() => scrollToSection("soups")}
            >
              Soups
            </button>
          </li>
          <li>
            <button
              className={activeSection === "specialties" ? "active" : ""}
              onClick={() => scrollToSection("specialties")}
            >
              Specialties
            </button>
          </li>
          <li>
            <button
              className={activeSection === "treats" ? "active" : ""}
              onClick={() => scrollToSection("treats")}
            >
              Treats
            </button>
          </li>
          <li>
            <button
              className={activeSection === "fridge" ? "active" : ""}
              onClick={() => scrollToSection("fridge")}
            >
              Fridge Items
            </button>
          </li>
        </ul>
      </div>
    </div>
  );

  // Render a deli item. When no explicit price is provided, show a "by the lb"
  // hint so visitors understand the item is sold by weight instead of thinking
  // a price is missing.
  const DeliItem = ({ name, description, price, byWeight = true }) => (
    <div className="deli-item">
      <div className="deli-item-header">
        <h3>{name}</h3>
        {price ? (
          <span className="deli-price">{price}</span>
        ) : byWeight ? (
          <span className="deli-price-hint">by the lb</span>
        ) : null}
      </div>
      {description && <p className="description">{description}</p>}
    </div>
  );

  return (
    <div className="menu-page deli-menu-page">
      {/* Page Header */}
      <section className="page-header">
        <div className="container">
          <h1 className="force-revue">Deli Menu</h1>
          <p>Premium deli items, fresh salads, homemade soups, and more</p>

          <div className="call-notice">
            <h3>
              <FontAwesomeIcon icon={faPhoneAlt} /> Call For Bulk Pricing
            </h3>
            <p>All bulk deli items are priced by weight. Please call for current pricing.</p>
            <p>Please call if you do not see what you are looking for, or if you have a custom request.</p>
          </div>
        </div>
      </section>

      {/* Menu Categories Navigation */}
      <DeliNavBar />

      {/* Weight Notice */}
      <div className="weight-notice">
        <div className="container">
          <FontAwesomeIcon icon={faWeightHanging} />
          <p>Most deli items are sold by weight. Call for current pricing unless price is otherwise indicated.</p>
        </div>
      </div>

      {/* Meats Section */}
      <CollapsibleSection
        id="meats"
        title="Meats By Weight"
        description="Premium deli meats, sold by weight"
        sectionRef={sectionRefs["meats"]}
        isActive={activeSection === "meats"}
        className="menu-section"
        isExpanded={expandedSections.meats}
        onToggle={() => toggleSection("meats")}
      >
        <div className="deli-items-grid">
          {dataWithOverrides.meats.map(item => (
            <DeliItem
              key={item.id}
              name={item.name}
              description={item.description}
            />
          ))}
        </div>
      </CollapsibleSection>


      {/* Cheeses Section */}

      <CollapsibleSection
        id="cheeses"
        title="Cheeses By Weight"
        description="Premium cheeses, sold by weight"
        sectionRef={sectionRefs["cheeses"]}
        isActive={activeSection === "cheeses"}
        className="menu-section"
        isExpanded={expandedSections.cheeses}
        onToggle={() => toggleSection("cheeses")}
      >
        <div className="deli-items-grid">
          {dataWithOverrides.cheeses.map(item => (
            <DeliItem
              key={item.id}
              name={item.name}
              description={item.description}
            />
          ))}
        </div>
      </CollapsibleSection>

      {/* Breads Section */}
      <CollapsibleSection
        id="breads"
        title="Breads"
        description="Fresh baked daily"
        sectionRef={sectionRefs["breads"]}
        isActive={activeSection === "breads"}
        className="menu-section"
        isExpanded={expandedSections.breads}
        onToggle={() => toggleSection("breads")}
      >
        <div className="deli-items-grid">
          {dataWithOverrides.breads.map(item => {
            if (item.id === 1) { // Assuming ID 1 is "Fresh Baked Rye Bread"
              return (
                <div key={item.id} className="deli-item rye-bread-custom">
                  <div className="deli-item-header">
                    <h3>{item.name}</h3>
                  </div>
                  <div className="rye-bread-details">
                    <p className="rye-bake-type">Twice Baked</p>
                    <p className="rye-bake-type">Single Baked</p>
                    <p className="rye-availability">Available as: whole loaf, half loaf, or by the slice</p>
                  </div>
                </div>
              );
            }
            return (
              <DeliItem
                key={item.id}
                name={item.name}
                description={item.description}
              />
            );
          })}
        </div>
      </CollapsibleSection>

      {/* Condiments Section */}
      <CollapsibleSection
        id="condiments"
        title="Condiments By Weight"
        description="Homemade and specialty condiments"
        sectionRef={sectionRefs["condiments"]}
        isActive={activeSection === "condiments"}
        className="menu-section"
        isExpanded={expandedSections.condiments}
        onToggle={() => toggleSection("condiments")}
      >
        <div className="deli-items-grid">
          {dataWithOverrides.condiments.map(item => (
            <DeliItem
              key={item.id}
              name={item.name}
              description={item.description}
            />
          ))}
        </div>
      </CollapsibleSection>

      {/* Smoked Fish Section */}
      <CollapsibleSection
        id="fish"
        title="Fresh Smoked Fish By Weight"
        description="Premium smoked fish, sold by weight"
        sectionRef={sectionRefs["fish"]}
        isActive={activeSection === "fish"}
        className="menu-section"
        isExpanded={expandedSections.fish}
        onToggle={() => toggleSection("fish")}
      >
        <div className="deli-items-grid">
          {dataWithOverrides.fish.map(item => (
            <DeliItem
              key={item.id}
              name={item.name}
              description={item.description}
            />
          ))}
        </div>
        <p className="note">*Lox is cured, not cooked</p>
      </CollapsibleSection>

      {/* Salads Section */}
      <CollapsibleSection
        id="salads"
        title="Homemade Salads By Weight"
        description="Freshly made salads, sold by weight"
        sectionRef={sectionRefs["salads"]}
        isActive={activeSection === "salads"}
        className="menu-section"
        isExpanded={expandedSections.salads}
        onToggle={() => toggleSection("salads")}
      >
        <div className="deli-items-grid">
          {dataWithOverrides.homemadeSalads.map(item => (
            <DeliItem
              key={item.id}
              name={item.name}
              description={item.description}
            />
          ))}
        </div>
      </CollapsibleSection>

      {/* Salad Plates Section */}
      <CollapsibleSection
        id="saladPlates"
        title="Salad Plates"
        description="Freshly prepared salad plates"
        sectionRef={sectionRefs["saladPlates"]}
        isActive={activeSection === "saladPlates"}
        className="menu-section"
        isExpanded={expandedSections.saladPlates}
        onToggle={() => toggleSection("saladPlates")}
      >
        <p className="note">Chopped/Tossed Salad . . . $1 extra</p>
        <div className="deli-items-list">
          {dataWithOverrides.saladPlates.map(item => (
            <div key={item.id} className="deli-item">
              <div className="deli-item-header">
                <h3>{item.name}</h3>
                <span className="deli-price">{item.price}</span>
              </div>
              <p>{item.description}</p>
            </div>
          ))}
        </div>
        <h3 className="subsection-title">Salad Dressings</h3>
        <p className="description">
          Russian, Ranch, Fat Free Ranch, Greek, Italian, Fat Free Italian, Balsamic Vinegar,
          Fat Free Raspberry Vinaigrette, Blue Cheese, Honey Mustard, Creamy Caesar*
        </p>
      </CollapsibleSection>

      {/* Soups Section */}
      <CollapsibleSection
        id="soups"
        title="Homemade Hot Soups"
        description="Made fresh daily"
        sectionRef={sectionRefs["soups"]}
        isActive={activeSection === "soups"}
        className="menu-section"
        isExpanded={expandedSections.soups}
        onToggle={() => toggleSection("soups")}
      >
        <div className="deli-items-grid">
          {dataWithOverrides.soups.map(item => (
            <DeliItem
              key={item.id}
              name={item.name}
              description={item.description}
            />
          ))}
        </div>
      </CollapsibleSection>

      {/* Deli Specialties Section */}
      <CollapsibleSection
        id="specialties"
        title="Deli Specialties & Side Items"
        description="Traditional deli favorites"
        sectionRef={sectionRefs["specialties"]}
        isActive={activeSection === "specialties"}
        className="menu-section"
        isExpanded={expandedSections.specialties}
        onToggle={() => toggleSection("specialties")}
      >
        <div className="deli-items-grid">
          {dataWithOverrides.specialties.map(item => (
            <DeliItem
              key={item.id}
              name={item.name}
              description={item.description}
            />
          ))}
        </div>
      </CollapsibleSection>

      {/* Treats & Pantry Section */}
      <CollapsibleSection
        id="treats"
        title="Deli Treats & Pantry Items"
        description="Sweet treats and pantry staples"
        sectionRef={sectionRefs["treats"]}
        isActive={activeSection === "treats"}
        className="menu-section"
        isExpanded={expandedSections.treats}
        onToggle={() => toggleSection("treats")}
      >
        <div className="deli-items-grid">
          {dataWithOverrides.treats.map(item => (
            <DeliItem
              key={item.id}
              name={item.name}
              description={item.description}
            />
          ))}
        </div>
      </CollapsibleSection>

      {/* Fridge Products Section */}
      <CollapsibleSection
        id="fridge"
        title="Fridge Products"
        description="Refrigerated grocery items"
        sectionRef={sectionRefs["fridge"]}
        isActive={activeSection === "fridge"}
        className="menu-section"
        isExpanded={expandedSections.fridge}
        onToggle={() => toggleSection("fridge")}
      >
        <div className="deli-items-grid">
          {dataWithOverrides.fridgeItems.map(item => (
            <DeliItem
              key={item.id}
              name={item.name}
              description={item.description}
            />
          ))}
        </div>
      </CollapsibleSection>

      {/* Call to Action */}
      <section className="cta-section">
        <div className="container">
          <h2>Looking for Sandwiches?</h2>
          <p>
            Check out our sandwich menu for our famous build-your-own and
            specialty options
          </p>
          <a href="/menu" className="btn primary">
            View Sandwich Menu
          </a>
        </div>
      </section>
      <div className="food-safety-disclaimer">
        <div className="container">
          <p>*CONSUMING RAW OR UNDERCOOKED MEATS, POULTRY, SEAFOOD, SHELLFISH, OR EGGS MAY INCREASE YOUR RISK OF FOODBORNE ILLNESS, ESPECIALLY IF YOU HAVE CERTAIN MEDICAL CONDITIONS.</p>
        </div>
      </div>
    </div>
  );
};

export default DeliMenuPage;
